package br.com.heyagenda.data.remote

import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.DocumentSnapshot
import dev.gitlive.firebase.firestore.FirebaseFirestore
import dev.gitlive.firebase.firestore.firestore
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.flow.map
import kotlinx.serialization.Serializable

@Serializable
data class StudioServiceDto(
    val id: String = "",
    val name: String = "",
    val category: String = "Geral",
    val priceFormatted: String = "",
    val durationMinutes: Int = 30,
    val description: String = ""
)

interface FirestoreServiceDataSource {
    fun observeServices(uid: String): Flow<List<StudioServiceDto>>
    suspend fun getServices(uid: String): List<StudioServiceDto>
    suspend fun saveService(uid: String, service: StudioServiceDto): Result<Unit>
    suspend fun deleteService(uid: String, serviceId: String): Result<Unit>
}

class FirestoreServiceDataSourceImpl : FirestoreServiceDataSource {

    private val firestore: FirebaseFirestore? by lazy {
        try {
            Firebase.firestore
        } catch (e: Throwable) {
            println(">>> [FirestoreService ERROR] Failed to get Firebase.firestore: ${e.message}")
            null
        }
    }

    override suspend fun getServices(uid: String): List<StudioServiceDto> {
        val fs = firestore ?: return emptyList()
        return try {
            val snapshot = fs.collection("professionals")
                .document(uid)
                .collection("services")
                .get()
            val list = snapshot.documents.mapNotNull { parseServiceDto(it) }
            println(">>> [FirestoreService GET] Fetched ${list.size} services directly for $uid")
            list
        } catch (e: Throwable) {
            println(">>> [FirestoreService GET ERROR] $uid: ${e.message}")
            emptyList()
        }
    }

    override fun observeServices(uid: String): Flow<List<StudioServiceDto>> = flow {
        val fs = firestore
        if (fs == null) {
            println(">>> [FirestoreService ERROR] firestore is NULL in observeServices")
            emit(emptyList())
            return@flow
        }

        // 1. Emit direct get result first for instant availability
        val direct = getServices(uid)
        emit(direct)

        // 2. Stream real-time updates safely
        try {
            fs.collection("professionals")
                .document(uid)
                .collection("services")
                .snapshots
                .map { snapshot ->
                    snapshot.documents.mapNotNull { parseServiceDto(it) }
                }
                .catch { e ->
                    println(">>> [FirestoreService SNAPSHOT ERROR] $uid: ${e.message}")
                }
                .collect { list ->
                    emit(list)
                }
        } catch (e: Throwable) {
            println(">>> [FirestoreService SNAPSHOT SETUP ERROR] ${e.message}")
        }
    }

    private fun parseServiceDto(doc: DocumentSnapshot): StudioServiceDto? {
        return try {
            val id = doc.id

            // Extract Name (try: name, title, nome, serviceName)
            val name: String = try {
                doc.get<String?>("name")?.takeIf { it.isNotBlank() }
            } catch (_: Throwable) { null }
                ?: try {
                    doc.get<String?>("title")?.takeIf { it.isNotBlank() }
                } catch (_: Throwable) { null }
                ?: try {
                    doc.get<String?>("nome")?.takeIf { it.isNotBlank() }
                } catch (_: Throwable) { null }
                ?: try {
                    doc.get<String?>("serviceName")?.takeIf { it.isNotBlank() }
                } catch (_: Throwable) { null }
                ?: "Serviço"

            // Extract Category
            val category: String = try {
                doc.get<String?>("category")?.takeIf { it.isNotBlank() }
            } catch (_: Throwable) { null }
                ?: try {
                    doc.get<String?>("categoria")?.takeIf { it.isNotBlank() }
                } catch (_: Throwable) { null }
                ?: "Geral"

            // Extract Price (try priceFormatted, price, valor, preco - handles Strings and Numbers)
            var priceFormatted: String? = try {
                doc.get<String?>("priceFormatted")?.takeIf { it.isNotBlank() }
            } catch (_: Throwable) { null }

            if (priceFormatted.isNullOrBlank()) {
                priceFormatted = try {
                    doc.get<String?>("price")?.takeIf { it.isNotBlank() }
                } catch (_: Throwable) { null }
                    ?: try {
                        doc.get<String?>("valor")?.takeIf { it.isNotBlank() }
                    } catch (_: Throwable) { null }
                    ?: try {
                        doc.get<String?>("preco")?.takeIf { it.isNotBlank() }
                    } catch (_: Throwable) { null }
            }

            if (priceFormatted.isNullOrBlank()) {
                val numPrice: Double? = try {
                    doc.get<Double?>("price")
                } catch (_: Throwable) {
                    try {
                        doc.get<Long?>("price")?.toDouble()
                    } catch (_: Throwable) {
                        try {
                            doc.get<Int?>("price")?.toDouble()
                        } catch (_: Throwable) { null }
                    }
                } ?: try {
                    doc.get<Double?>("valor")
                } catch (_: Throwable) {
                    try {
                        doc.get<Long?>("valor")?.toDouble()
                    } catch (_: Throwable) {
                        try {
                            doc.get<Int?>("valor")?.toDouble()
                        } catch (_: Throwable) { null }
                    }
                }

                if (numPrice != null) {
                    val intPart = numPrice.toLong()
                    val decPart = ((numPrice - intPart) * 100).toLong()
                    priceFormatted = if (decPart == 0L) {
                        "R$ $intPart,00"
                    } else {
                        "R$ $intPart,${decPart.toString().padStart(2, '0')}"
                    }
                }
            }

            if (priceFormatted.isNullOrBlank()) {
                priceFormatted = "A combinar"
            } else if (!priceFormatted.startsWith("R$")) {
                priceFormatted = "R$ $priceFormatted"
            }

            // Extract Duration (try durationMinutes, duration, duracao - handles Int, Long, Double, String)
            val durationMinutes: Int = try {
                doc.get<Int?>("durationMinutes")
            } catch (_: Throwable) { null }
                ?: try {
                    doc.get<Long?>("durationMinutes")?.toInt()
                } catch (_: Throwable) { null }
                ?: try {
                    doc.get<Double?>("durationMinutes")?.toInt()
                } catch (_: Throwable) { null }
                ?: try {
                    doc.get<Int?>("duration")
                } catch (_: Throwable) { null }
                ?: try {
                    doc.get<Long?>("duration")?.toInt()
                } catch (_: Throwable) { null }
                ?: try {
                    doc.get<Double?>("duration")?.toInt()
                } catch (_: Throwable) { null }
                ?: try {
                    doc.get<Int?>("duracao")
                } catch (_: Throwable) { null }
                ?: try {
                    val str = doc.get<String?>("duration")
                    str?.filter { it.isDigit() }?.toIntOrNull()
                } catch (_: Throwable) { null }
                ?: 30

            // Extract Description
            val description: String = try {
                doc.get<String?>("description") ?: ""
            } catch (_: Throwable) {
                try {
                    doc.get<String?>("descricao") ?: ""
                } catch (_: Throwable) { "" }
            }

            StudioServiceDto(
                id = id,
                name = name,
                category = category,
                priceFormatted = priceFormatted,
                durationMinutes = durationMinutes,
                description = description
            )
        } catch (e: Throwable) {
            println(">>> [FirestoreService parseServiceDto fallback] ${e.message}")
            try {
                doc.data<StudioServiceDto>().copy(id = doc.id)
            } catch (_: Throwable) {
                null
            }
        }
    }

    override suspend fun saveService(uid: String, service: StudioServiceDto): Result<Unit> {
        val fs = firestore ?: return Result.failure(IllegalStateException("Firestore unavailable"))
        return try {
            val docRef = if (service.id.isBlank()) {
                fs.collection("professionals").document(uid).collection("services").document
            } else {
                fs.collection("professionals").document(uid).collection("services").document(service.id)
            }
            val finalDto = service.copy(id = docRef.id)

            docRef.set(
                mapOf(
                    "id" to finalDto.id,
                    "name" to finalDto.name,
                    "category" to finalDto.category,
                    "priceFormatted" to finalDto.priceFormatted,
                    "durationMinutes" to finalDto.durationMinutes,
                    "description" to finalDto.description
                )
            )
            println(">>> [FirestoreService SUCCESS] Saved service ${finalDto.id} for $uid")
            Result.success(Unit)
        } catch (e: Throwable) {
            println(">>> [FirestoreService SAVE ERROR] ${e.message}")
            Result.failure(e)
        }
    }

    override suspend fun deleteService(uid: String, serviceId: String): Result<Unit> {
        val fs = firestore ?: return Result.failure(IllegalStateException("Firestore unavailable"))
        return try {
            fs.collection("professionals")
                .document(uid)
                .collection("services")
                .document(serviceId)
                .delete()
            println(">>> [FirestoreService DELETE SUCCESS] Deleted service $serviceId for $uid")
            Result.success(Unit)
        } catch (e: Throwable) {
            println(">>> [FirestoreService DELETE ERROR] ${e.message}")
            Result.failure(e)
        }
    }
}
